import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CostAlertsAndHelpControls, Loading } from '@rd-web-markets/shared/dist/util';
import claimContractorsService from '../../services/claim/claim_contractors.service';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ChangeHighlighter } from '@rd-web-markets/shared/dist/util/ChangeHighlighter';
import { apiKey, getDefaultConfig } from '@rd-web-markets/shared/dist/util/tinyMceConfig';
import { Editor } from '@tinymce/tinymce-react';
import { ContractorsRow, ContractorsRowHeader, ContractorsTotalsRow } from '.';
import { useTranslation } from 'react-i18next';
export const ContractorsSection = _ref => {
  let {
    claim,
    refreshClaim,
    setClaim,
    debouncedSaveClaim,
    project
  } = _ref;
  const [claimContractors, setClaimContractors] = useState(null);
  const dispatch = useDispatch();
  const claimId = useMemo(() => claim?.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const {
    t
  } = useTranslation();
  useEffect(() => {
    async function getData() {
      try {
        if (claimId) {
          setClaimContractors(await claimContractorsService.all(claimId, project.id));
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    }
    getData();
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const contractor = claimContractors[index];
    if (contractor.id) {
      try {
        await claimContractorsService.delete(contractor);
        const contractors = await claimContractorsService.all(claim.id, project.id);
        setClaimContractors(contractors);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newContractors = [...claimContractors];
      newContractors.splice(index, 1);
      setClaimContractors(newContractors);
    }
  };
  const handleAdd = contractor => {
    setClaimContractors([...claimContractors, contractor]);
  };
  const handleImport = async data => {
    for (const line of data.split('\n')) {
      if (line.length === 0) {
        continue;
      }
      const lineSplit = line.split('\t');
      const newSubcontractor = await claimContractorsService.create({
        claim_id: claim.id,
        name: lineSplit[0],
        net_amount: (lineSplit[1] || '').replace(',', '')
      });
      setClaimContractors([...claimContractors, newSubcontractor]);
    }
  };
  if (!claim || !claimContractors) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(CostAlertsAndHelpControls, {
    claim: claim,
    onSubmit: handleImport,
    extraDescription: "After the import process is finished, dont forget to mark off any connected subcontractors and set their R&D allocation."
  }), claimContractors.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(ContractorsRowHeader, null)), /*#__PURE__*/React.createElement("tbody", null, claimContractors.map((contractor, index) => {
    return /*#__PURE__*/React.createElement(ContractorsRow, {
      key: index,
      index: index,
      claim: claim,
      handleDelete: () => handleDelete(index),
      claimContractors: claimContractors,
      setClaimContractors: setClaimContractors,
      refreshClaim: refreshClaim,
      handleError: handleError
    });
  }), /*#__PURE__*/React.createElement(ContractorsTotalsRow, {
    claim: claim
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_project_report_id: project.id
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "add"), " Add Row"))), /*#__PURE__*/React.createElement(Card, {
    className: "mt-3"
  }, /*#__PURE__*/React.createElement(Card.Header, null, t('notes')), /*#__PURE__*/React.createElement(ChangeHighlighter, {
    as: Editor,
    apiKey: apiKey,
    onEditorChange: val => {
      setClaim({
        ...claim,
        contractor_notes: val
      });
    },
    onKeyUp: debouncedSaveClaim,
    disabled: claim.is_locked || claim.financials_locked,
    object: claim,
    name: "contractor_notes",
    init: getDefaultConfig()
  }))), /*#__PURE__*/React.createElement(Card.Footer, null, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-start"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "light",
    to: `/${user.account_type}/claims/${claim.id}/financial_details/wages`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_left"), " Back")), /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-end"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "primary",
    to: `/${user.account_type}/claims/${claim.id}/financial_details/total_costs`
  }, "Continue ", /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_right"))))));
};